import React, { useMemo, useState } from 'react'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import ButtonField from 'components_new/molecules/ButtonField'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Link from 'components_new/atoms/Link'
import List from 'components_new/atoms/List'
import ListItem from 'components_new/atoms/List/ListItem'
import ListItemAvatar from 'components_new/atoms/List/ListItemAvatar'
import ListItemSecondaryAction from 'components_new/atoms/List/ListItemSecondaryAction'
import ListItemText from 'components_new/atoms/List/ListItemText'
import Skeleton from 'components_new/atoms/Skeleton'
import TextField from 'components_new/atoms/TextField'

import CustomListItem from 'components_new/molecules/GenericListItem'
import AdvancedDialog from 'components_new/molecules/AdvancedDialog'
import SelectSearch from 'components_new/molecules/SelectSearch'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import SwitchItem from 'components_new/molecules/SwitchItem'
import TabSwitch from 'components_new/molecules/TabSwitch'
import UploadFile from 'components_new/molecules/UploadFile'

import EditEconomySetsDialog from 'components_new/organisms/dialogs/EditEconomySetsDialog'

import type { TCompanyDto } from '@homepal/api-contract'
import { ParsedEconomySet } from 'types/GlobalEconomySets'
import { useTranslation } from 'language/translator'

import {
  useHasIntegratedCompanyGroups,
  useIsManagementCompany
} from 'redux/hooks/Customer'
import { useCompanyGroups } from 'redux/hooks/CompanyGroups'
import { useCompaniesData } from 'redux/hooks/Companies'
import { useEconomyAccountActions } from 'redux/actions/EconomyAccounts'
import { useEconomySetActions } from 'redux/actions/EconomySets'
import { useEconomySets } from 'redux/hooks/EconomySets'
import { useCompanyGroupActions } from 'redux/actions/CompanyGroups'

enum AccountsSource {
  FILE = 'FILE',
  MANUAL = 'MANUAL'
}

const ChartsOfAccounts = () => {
  // Actions
  const {
    tryCreateEconomySet,
    tryDeleteEconomySet,
    tryDuplicateEconomySet,
    tryUpdateEconomySet,
    tryUpdateEconomySetCompanies,
    tryUploadEconomySet
  } = useEconomySetActions()

  const {
    tryCreateEconomyAccount,
    tryDeleteEconomyAccount,
    tryUpdateEconomyAccount
  } = useEconomyAccountActions()

  const { tryUpdateCompanyGroup } = useCompanyGroupActions()

  const { t } = useTranslation()

  const hasIntegatedCompanyGroups = useHasIntegratedCompanyGroups()
  const isManangementCompany = useIsManagementCompany()
  const [economySets, loadingEconomySets] = useEconomySets()
  const [companyGroups, loadingCompanyGroups] = useCompanyGroups()
  const companiesData = useCompaniesData()

  // Loaders
  const [creatingSet, setCreatingSet] = useState<boolean>(false)
  const [deletingSet, setDeletingSet] = useState<boolean>(false)
  const [duplicatingSet, setDuplicatingSet] = useState<boolean>(false)

  const [economySetToDelete, setEconomySetsToDelete] =
    useState<ParsedEconomySet | null>(null)
  const [economySetToDuplicate, setEconomySetToDuplicate] =
    useState<ParsedEconomySet | null>(null)
  const [duplicateName, setDuplicateName] = useState('')
  const [duplicateReferences, setDuplicateReferences] = useState(true)
  const [duplicateCompanyIds, setDuplicateCompanyIds] = useState<string[]>([])
  const [economySetToEdit, setEconomySetsToEdit] = useState<string | null>(null)
  const [editAccountsOnOpen, setEditAccountsOnOpen] = useState(false)
  const [createEconomySets, setCreateEconomySets] = useState(false)
  const [newEconomySetsName, setNewEconomySetsName] = useState('')
  const [newEconomySetsCompanyIds, setNewEconomySetsCompanyIds] = useState<
    string[]
  >([])
  const [newEconomySetsSource, setNewEconomySetsSource] =
    useState<AccountsSource>(AccountsSource.FILE)
  const [
    showCompanyGroupsWithoutEconomySets,
    setShowCompanyGroupsWithoutEconomySets
  ] = useState(false)
  const [newEconomySetsFile, setNewEconomySetsFile] = useState<{
    file: Blob
    name: string
  } | null>(null)

  const parsedEconomySets: ParsedEconomySet[] = useMemo(() => {
    return economySets
      .map((set) => {
        return {
          ...set,
          companyGroups: companyGroups.filter(
            (group) => group.economy_set_id === set.id
          ),
          // Deactivated companies keep their link but are not shown.
          companies: set.company_ids
            .map((id) => companiesData[id])
            .filter((company) => company && !company.ignore)
        }
      })
      .sort((a, b) => Number(b.is_default) - Number(a.is_default))
  }, [economySets, companyGroups, companiesData])

  // Companies that can be put on a company-specific chart of accounts: active
  // companies not already on another one.
  const getAvailableCompanies = (economySetId: string | null) => {
    const taken = new Set(
      economySets
        .filter((set) => set.id !== economySetId)
        .flatMap((set) => set.company_ids)
    )

    return Object.values(companiesData).filter(
      (company: TCompanyDto) => !company.ignore && !taken.has(company.id)
    )
  }

  const getBody = (set: ParsedEconomySet) => {
    if (isManangementCompany) {
      return `${set.companyGroups.length} ${t(
        'global',
        set.companyGroups.length === 1 ? 'company_group' : 'company_groups',
        true
      )}`
    }

    if (set.is_default) {
      return t('chart_of_accounts', 'main_chart_of_accounts')
    }

    return set.companies.length > 0
      ? t('chart_of_accounts', '{{count}}_companies', false, {
        count: set.companies.length.toString()
      })
      : t('chart_of_accounts', 'no_companies')
  }

  const companyGroupsWithoutEconomySets = useMemo(
    () => companyGroups.filter((group) => !group.economy_set_id),
    [companyGroups]
  )

  const handleCloseCreateEconomySets = () => {
    setCreateEconomySets(false)

    setNewEconomySetsFile(null)
    setNewEconomySetsName('')
    setNewEconomySetsCompanyIds([])
    setNewEconomySetsSource(AccountsSource.FILE)
  }

  // Every extra chart of accounts of a regular company is company-specific.
  const requiresCompanies = !isManangementCompany

  const renderCompanySelect = (
    selectedIds: string[],
    onChange: (ids: string[]) => void
  ) =>
    requiresCompanies ? (
      <SelectSearch
        closeOnSelect={false}
        helperText={t('chart_of_accounts', 'companies_required')}
        label={t('global', 'companies')}
        multiple={true}
        onChange={(options) =>
          onChange(options.map((option) => option.id as string))
        }
        options={getAvailableCompanies(null).map((company) => ({
          id: company.id,
          title: company.name
        }))}
        selected={selectedIds.map((id) => ({
          id,
          title: companiesData[id]?.name ?? id
        }))}
        sx={{ mb: 2 }}
      />
    ) : null

  const handleOpenDuplicateEconomySet = (set: ParsedEconomySet) => {
    setEconomySetToDuplicate(set)

    setDuplicateName(
      t('chart_of_accounts', '{{name}}_copy', false, { name: set.name })
    )
    setDuplicateReferences(true)
    setDuplicateCompanyIds([])
  }

  const handleCloseDuplicateEconomySet = () => {
    setEconomySetToDuplicate(null)

    setDuplicateName('')
  }

  const handleDuplicateEconomySet = () => {
    const set = economySetToDuplicate as ParsedEconomySet

    setDuplicatingSet(true)
    tryDuplicateEconomySet(
      set.id,
      duplicateName,
      duplicateReferences,
      requiresCompanies ? duplicateCompanyIds : undefined,
      (err) => {
        if (!err) {
          handleCloseDuplicateEconomySet()
        }

        setDuplicatingSet(false)
      }
    )
  }

  const loading = loadingEconomySets || loadingCompanyGroups

  if (loading) {
    return <Skeleton variant="rounded" width="100%" height={98} />
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: '100%'
        }}
      >
        {parsedEconomySets.map((set) => (
          <CustomListItem
            actions={
              <>
                <IconButton
                  onClick={() => setEconomySetsToEdit(set.id)}
                  size="small"
                >
                  <Icon fontSize="small" name="Edit" />
                </IconButton>
                <IconButton
                  onClick={() => handleOpenDuplicateEconomySet(set)}
                  size="small"
                >
                  <Icon fontSize="small" name="Duplicate" />
                </IconButton>
                {set.is_default ? null : (
                  <IconButton
                    onClick={() => setEconomySetsToDelete(set)}
                    size="small"
                  >
                    <Icon fontSize="small" name="Delete" />
                  </IconButton>
                )}
              </>
            }
            border={true}
            body={getBody(set)}
            component="div"
            hasHover={true}
            icon={{
              name: 'File'
            }}
            key={set.id}
            size="medium"
            title={set.name}
            variant="file"
          />
        ))}

        <ButtonField
          icon={{ name: 'Add' }}
          onClick={() => setCreateEconomySets(true)}
        />

        {isManangementCompany && companyGroupsWithoutEconomySets.length > 0 ? (
          <strong>
            <Link
              color="inherit"
              // eslint-disable-next-line max-len
              onClick={() => setShowCompanyGroupsWithoutEconomySets(true)}
            >
              {t(
                'chart_of_accounts',
                '{{count}}_missing_chart_of_accounts',
                false,
                // eslint-disable-next-line max-len
                { count: companyGroupsWithoutEconomySets.length.toString() }
              )}
            </Link>
          </strong>
        ) : undefined}
      </Box>

      <SimpleDialog
        title={t('chart_of_accounts', 'delete_chart_of_accounts_title')}
        open={Boolean(economySetToDelete)}
        onClose={() => setEconomySetsToDelete(null)}
        actions={
          <>
            <Button variant="text" onClick={() => setEconomySetsToDelete(null)}>
              {t('global', 'close')}
            </Button>
            <Button
              color="error"
              loading={deletingSet}
              onClick={() => {
                setDeletingSet(true)
                tryDeleteEconomySet(
                  (economySetToDelete as ParsedEconomySet).id,
                  (err) => {
                    if (!err) {
                      setEconomySetsToDelete(null)
                    }

                    setDeletingSet(false)
                  }
                )
              }}
              variant="contained"
            >
              {t('global', 'delete')}
            </Button>
          </>
        }
        contentText={t(
          'chart_of_accounts',
          isManangementCompany
            ? 'delete_chart_of_accounts_body'
            : 'delete_company_specific_chart_of_accounts_body'
        )}
      />

      <AdvancedDialog
        actions={
          <Button
            variant="text"
            onClick={() => setShowCompanyGroupsWithoutEconomySets(false)}
          >
            {t('global', 'close')}
          </Button>
        }
        onClose={() => setShowCompanyGroupsWithoutEconomySets(false)}
        open={showCompanyGroupsWithoutEconomySets}
        title={t(
          'chart_of_accounts',
          '{{count}}_missing_chart_of_accounts',
          false,
          // eslint-disable-next-line max-len
          { count: companyGroupsWithoutEconomySets.length.toString() }
        )}
      >
        <List>
          {companyGroupsWithoutEconomySets.map((group) => (
            <ListItem key={group.id}>
              <Icon color="disabled" name="Business" />
              <ListItemText
                sx={{ ml: 3 }}
                primary={group.name}
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItem>
          ))}
        </List>
      </AdvancedDialog>

      <SimpleDialog
        actions={
          <Button variant="text" onClick={handleCloseCreateEconomySets}>
            {t('global', 'close')}
          </Button>
        }
        onClose={handleCloseCreateEconomySets}
        open={createEconomySets}
        title={t('chart_of_accounts', 'create_chart_of_accounts')}
      >
        <TabSwitch
          color="primary"
          options={[
            {
              value: AccountsSource.FILE,
              label: t('chart_of_accounts', 'upload_file')
            },
            {
              value: AccountsSource.MANUAL,
              label: t('chart_of_accounts', 'add_accounts_manually')
            }
          ]}
          value={newEconomySetsSource}
          onChange={(value) => setNewEconomySetsSource(value)}
          sx={{ mb: 2 }}
        />
        <Alert severity="info" icon={<Icon name="Info" />}>
          {[
            requiresCompanies
              ? t('chart_of_accounts', 'company_specific_instructions')
              : null,
            newEconomySetsSource === AccountsSource.FILE
              ? t('chart_of_accounts', 'upload_instructions')
              : t('chart_of_accounts', 'manual_instructions')
          ]
            .filter(Boolean)
            .join(' ')}
        </Alert>
        <TextField
          label={t('global', 'name')}
          required={true}
          autoComplete="off"
          value={newEconomySetsName}
          onChange={(event) => setNewEconomySetsName(event.target.value)}
          sx={{ mt: 2, mb: 2 }}
        />
        {renderCompanySelect(
          newEconomySetsCompanyIds,
          setNewEconomySetsCompanyIds
        )}
        {newEconomySetsSource === AccountsSource.FILE ? (
          <UploadFile
            accept={['text/csv']}
            maxSize={2}
            uploadFile={(file) =>
              setNewEconomySetsFile({ file, name: file.name })
            }
          />
        ) : null}
        {newEconomySetsSource === AccountsSource.FILE && newEconomySetsFile ? (
          <ListItem component="div" sx={{ bgcolor: 'action.hover', mt: 1 }}>
            <ListItemAvatar>
              <Box
                sx={{
                  position: 'relative',
                  height: '48px',
                  width: '48px',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Icon name={'AttachmentOutlined'} />
              </Box>
            </ListItemAvatar>
            <ListItemText primary={newEconomySetsFile.name} />
            <ListItemSecondaryAction>
              <IconButton onClick={() => setNewEconomySetsFile(null)}>
                <Icon name="Delete" />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ) : null}
        <Button
          fullWidth
          disabled={
            !Boolean(newEconomySetsName) ||
            (newEconomySetsSource === AccountsSource.FILE &&
              !Boolean(newEconomySetsFile)) ||
            (requiresCompanies && newEconomySetsCompanyIds.length === 0)
          }
          loading={creatingSet}
          variant="contained"
          size="large"
          sx={{ mt: 2 }}
          onClick={() => {
            const manual = newEconomySetsSource === AccountsSource.MANUAL

            setCreatingSet(true)
            tryCreateEconomySet(
              newEconomySetsName,
              manual ? null : (newEconomySetsFile?.file as Blob),
              requiresCompanies ? newEconomySetsCompanyIds : undefined,
              (err, id) => {
                if (!err) {
                  handleCloseCreateEconomySets()

                  // Continue straight to adding the accounts one by one.
                  if (manual && id) {
                    setEditAccountsOnOpen(true)
                    setEconomySetsToEdit(id)
                  }
                }

                setCreatingSet(false)
              }
            )
          }}
        >
          {t('global', 'create')}
        </Button>
      </SimpleDialog>

      <SimpleDialog
        actions={
          <Button variant="text" onClick={handleCloseDuplicateEconomySet}>
            {t('global', 'close')}
          </Button>
        }
        onClose={handleCloseDuplicateEconomySet}
        open={Boolean(economySetToDuplicate)}
        title={t('chart_of_accounts', 'duplicate_chart_of_accounts')}
      >
        <Alert severity="info" icon={<Icon name="Info" />}>
          {t('chart_of_accounts', 'duplicate_chart_of_accounts_body')}
        </Alert>
        <TextField
          label={t('global', 'name')}
          required={true}
          autoComplete="off"
          value={duplicateName}
          onChange={(event) => setDuplicateName(event.target.value)}
          sx={{ mt: 2, mb: requiresCompanies ? 2 : 1 }}
        />
        {renderCompanySelect(duplicateCompanyIds, setDuplicateCompanyIds)}
        <SwitchItem
          checked={duplicateReferences}
          label={t('chart_of_accounts', 'duplicate_account_references')}
          onChange={() => setDuplicateReferences(!duplicateReferences)}
        />
        <Button
          fullWidth
          disabled={
            !Boolean(duplicateName) ||
            (requiresCompanies && duplicateCompanyIds.length === 0)
          }
          loading={duplicatingSet}
          variant="contained"
          size="large"
          sx={{ mt: 2 }}
          onClick={handleDuplicateEconomySet}
        >
          {t('global', 'duplicate')}
        </Button>
      </SimpleDialog>

      {economySetToEdit ? (
        <EditEconomySetsDialog
          economySet={
            parsedEconomySets.find(
              (item) => item.id === economySetToEdit
            ) as ParsedEconomySet
          }
          companyGroupsWithoutEconomySets={companyGroupsWithoutEconomySets}
          availableCompanies={getAvailableCompanies(economySetToEdit)}
          updateCompanies={tryUpdateEconomySetCompanies}
          createEconomyAccount={tryCreateEconomyAccount}
          deleteEconomyAccount={tryDeleteEconomyAccount}
          integrateCompanyGroups={hasIntegatedCompanyGroups}
          open={true}
          openAccounts={editAccountsOnOpen}
          onClose={() => {
            setEconomySetsToEdit(null)
            setEditAccountsOnOpen(false)
          }}
          updateCompanyGroup={tryUpdateCompanyGroup}
          updateEconomyAccount={tryUpdateEconomyAccount}
          updateEconomySet={tryUpdateEconomySet}
          uploadEconomySet={tryUploadEconomySet}
        />
      ) : null}
    </>
  )
}

export default ChartsOfAccounts
